import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import api from '@/lib/api'
import { useAccessToken, useCurrentUser } from '@/features/auth/state/auth'

export interface CreateCompetenciaRestritaRequest {
  idApuracao: string
  periodoInicial: string
  periodoFinal: string
}

export interface CreateCompetenciaRestritaResponse {
  statusCode: number
  message: string
  data: {
    id_apuracao: string
    id_apuracao_competencia: string
    id_competencia: string
    periodoInicial: string
    periodoFinal: string
    competencia: unknown | null
  }
}

interface ApiErrorPayload {
  message?: string | string[]
}

function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const payload = error.response?.data as ApiErrorPayload | undefined

    if (Array.isArray(payload?.message) && payload.message.length > 0) {
      return payload.message.join(', ')
    }

    if (typeof payload?.message === 'string' && payload.message.trim()) {
      return payload.message
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'Erro ao criar competência restrita'
}

export function useCreateCompetenciaRestrita() {
  const queryClient = useQueryClient()
  const token = useAccessToken()
  const user = useCurrentUser()

  return useMutation({
    mutationFn: async ({
      idApuracao,
      periodoInicial,
      periodoFinal
    }: CreateCompetenciaRestritaRequest): Promise<CreateCompetenciaRestritaResponse> => {
      if (!idApuracao?.trim()) {
        throw new Error('ID da apuração é obrigatório')
      }

      if (!periodoInicial?.trim() || !periodoFinal?.trim()) {
        throw new Error('Período inicial e final são obrigatórios')
      }

      if (!user || !token) {
        throw new Error('Usuário não autenticado')
      }

      try {
        const response = await api.post<CreateCompetenciaRestritaResponse>(
          `/competencia/criar/restrito/${idApuracao}`,
          { periodoInicial, periodoFinal },
          { headers: { Authorization: `Bearer ${token}` } }
        )

        if (!response.data) {
          throw new Error('Erro ao criar competência restrita')
        }

        return response.data
      } catch (error) {
        throw new Error(getApiErrorMessage(error))
      }
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({ queryKey: ['apuracao', request.idApuracao] })
      queryClient.invalidateQueries({ queryKey: ['resume', request.idApuracao] })
    }
  })
}
